import React, { useEffect } from "react";
import {
  formatStakeBalance,
  getPackageIcon,
  getReturnAccruesLabel,
  getStakingTypeLabel,
  getUnbondingPeriodDays,
} from "./stakingStakeHelpers";
import "./staking.css";

const OverviewRow = ({ label, value, highlight }) => (
  <div className="stake-overview-row">
    <span className="stake-overview-row__label">{label}</span>
    <span className={`stake-overview-row__value${highlight ? " stake-overview-row__value--green" : ""}`}>
      {value}
    </span>
  </div>
);

const StakeConfirmOverview = ({
  selectedPackage,
  stakeAmount,
  estimatedDailyReturn,
  currentStakingAmount,
  isTopUp = false,
  loading = false,
  error = "",
  onBack,
  onConfirm,
}) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onBack?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack, loading]);

  if (!selectedPackage) return null;

  const currency = selectedPackage.currency || "";
  const currencyFullName = selectedPackage.currencyFullName || selectedPackage.fullName || selectedPackage.coinName || "—";
  const rewardCurrency = selectedPackage.rewardCurrency || currency;
  const stakingType = getStakingTypeLabel(selectedPackage.stakingType);
  const duration = selectedPackage.duration ?? selectedPackage.durationDays;
  const durationLabel = duration != null ? `${duration} Days` : "—";
  const unbondingDays = getUnbondingPeriodDays(selectedPackage);

  return (
    <div className="stake-overview-overlay" onClick={loading ? undefined : onBack}>
      <div className="stake-overview-content" onClick={(e) => e.stopPropagation()}>
        <div className="stake-overview-header">
          <div className="stake-overview-header__left">
            <div className="stake-overview-title">
              <img
                src={getPackageIcon(selectedPackage)}
                alt={currency}
                className="mint-title-icon"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `/images/staking_img/${String(currency || "usdt").toLowerCase()}_icon.svg`;
                }}
              />
              <span>
                {currency ? `${currency} ` : ""}{isTopUp ? "Staking Overview" : "Staking Overview"}
              </span>
            </div>
           
          </div>
          <button type="button" className="mint-modal-close" disabled={loading} onClick={onBack}>
            <i className="ri-close-line" />
          </button>
        </div>

        <p className="stake-overview-subtitle">
          {isTopUp
            ? "Please review your top-up details before confirming."
            : "You are about to stake the following package. Please review the details."}
        </p>

        <div className="stake-overview-body">
          <OverviewRow label="Currency" value={currency || "—"} />
          <OverviewRow label="Currency Name" value={currencyFullName} />
          <OverviewRow label="Staking Type" value={stakingType} />
          <OverviewRow
            label={isTopUp ? "Top Up Amount" : "Staking Amount"}
            value={`${formatStakeBalance(stakeAmount)} ${currency}`}
            highlight
          />
          <OverviewRow
            label="Est. APR"
            value={`${selectedPackage.returnPercentage ?? "—"}%`}
            highlight
          />
          <OverviewRow label="Duration" value={durationLabel} />
          <OverviewRow
            label="Est. Daily Return"
            value={`${estimatedDailyReturn} ${rewardCurrency}`}
            highlight
          />
          <OverviewRow
            label="Your current staking"
            value={`${formatStakeBalance(currentStakingAmount)} ${currency}`}
          />
          <OverviewRow
            label="Return Accrues"
            value={getReturnAccruesLabel(selectedPackage.interestStartsAfterDays)}
          />
          <OverviewRow label="Reward Distributes" value="Daily" />
          <OverviewRow
            label="Unbonding Period"
            value={`About ${unbondingDays} day(s)`}
          />
          <OverviewRow label="Wallet" value="Earning Wallet" />
          {selectedPackage.earlyWithdrawalPenalty > 0 ? (
            <OverviewRow
              label="Early Withdrawal Penalty"
              value={`${selectedPackage.earlyWithdrawalPenalty}%`}
            />
          ) : null}
        </div>

        {error ? (
          <div className="stake-overview-error">{error}</div>
        ) : null}

        <div className="stake-overview-actions">
          <button type="button" className="mint-btn-cancel" disabled={loading} onClick={onBack}>
            Back
          </button>
          <button
            type="button"
            className="mint-btn-confirm"
            disabled={loading}
            onClick={onConfirm}
          >
            {loading ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StakeConfirmOverview;
